import { useEffect, useRef, useState } from 'react';
import { Loader2, ShieldAlert, X } from 'lucide-react';
import { getDecisionFile } from '@generated/server/decisions';
import { useSession } from './session';
import { Logo } from './Logo';

export function ProtectedViewer({ lib, id, name, onClose }: { lib: 'itp' | 'sd'; id: string; name: string; onClose: () => void }) {
  const { sid } = useSession();
  const pagesRef = useRef<HTMLDivElement>(null);
  const [mark, setMark] = useState('');
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const block = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && ['p', 's', 'c'].includes(e.key.toLowerCase())) e.preventDefault();
    };
    window.addEventListener('keydown', block);
    (async () => {
      try {
        const r = await getDecisionFile({ data: { sid, lib, id } });
        if (cancelled || !r) return;
        setMark(r.watermark);
        const pdfjs = await import('pdfjs-dist');
        const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
        pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
        const bytes = Uint8Array.from(atob(r.base64), (c) => c.charCodeAt(0));
        const pdf = await pdfjs.getDocument({ data: bytes }).promise;
        const host = pagesRef.current;
        if (!host) return;
        host.innerHTML = '';
        for (let n = 1; n <= pdf.numPages; n++) {
          if (cancelled) return;
          const page = await pdf.getPage(n);
          const vp = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          canvas.width = vp.width;
          canvas.height = vp.height;
          canvas.className = 'mx-auto mb-4 block h-auto w-full max-w-[900px] shadow-lg';
          host.appendChild(canvas);
          const ctx = canvas.getContext('2d');
          if (ctx) await page.render({ canvasContext: ctx, viewport: vp, canvas }).promise;
        }
        setState('ready');
      } catch (e) {
        console.error(e);
        if (!cancelled) { setError(e instanceof Error ? e.message : 'The decision could not be opened.'); setState('error'); }
      }
    })();
    return () => { cancelled = true; window.removeEventListener('keydown', block); };
  }, [sid, lib, id]);

  return (
    <div className="no-select fixed inset-0 z-[60] flex flex-col bg-secondary" onContextMenu={(e) => e.preventDefault()}>
      <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3 text-secondary-foreground">
        <div className="rounded-lg bg-card px-2 py-1"><Logo size="sm" /></div>
        <p className="min-w-0 flex-1 truncate text-sm font-semibold">{name} <span className="font-normal opacity-60">— confidential, view only</span></p>
        <button onClick={onClose} aria-label="Close viewer" className="flex h-10 w-10 items-center justify-center rounded-lg hover:bg-white/10"><X className="h-5 w-5" /></button>
      </div>
      <div className="relative flex-1 overflow-auto bg-[hsl(var(--preview))] p-4">
        {state === 'loading' && <div className="flex h-full items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-secondary/50" /></div>}
        {state === 'error' && <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-destructive"><ShieldAlert className="h-8 w-8" />{error}</div>}
        <div className="relative">
          <div ref={pagesRef} />
          {state === 'ready' && (
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <div className="flex h-full flex-col justify-around">
                {Array.from({ length: 12 }).map((_, i) => (
                  <p key={i} className="-rotate-[24deg] whitespace-nowrap text-center text-xl font-semibold text-secondary/15">{mark}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
